import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { api } from "../lib/api";

function timeAgo(iso) {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/**
 * C-ORE Notification Center (2026-08) — minimal bell + dropdown in the
 * TopBar. Currently surfaces Overdue Task alerts (backend-authoritative,
 * see overdue_notifier.py); reusable for any future notification type.
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = useCallback(async () => {
    const res = await api.listNotifications();
    if (res.ok) {
      setItems(res.data.notifications || []);
      setUnread(res.data.unread_count || 0);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const onItemClick = async (n) => {
    if (n.is_read) return;
    const res = await api.markNotificationRead(n.id);
    if (res.ok) {
      setItems((prev) => prev.map((it) => (it.id === n.id ? { ...it, is_read: true } : it)));
      setUnread((u) => Math.max(0, u - 1));
    }
  };

  const onMarkAllRead = async () => {
    const res = await api.markAllNotificationsRead();
    if (res.ok) {
      setItems((prev) => prev.map((it) => ({ ...it, is_read: true })));
      setUnread(0);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="notification-bell-button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        className="relative inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-600 hover:bg-ink-100 transition-colors"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            data-testid="notification-bell-badge"
            className="absolute -top-0.5 -right-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-status-critical px-1 text-[10px] font-bold text-white"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 mt-2 w-80 max-h-[420px] overflow-y-auto rounded-md border border-ink-200 bg-white shadow-hover z-50"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-ink-100 sticky top-0 bg-white">
            <span className="text-[12px] font-semibold text-ink-950">Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                data-testid="notification-mark-all-read"
                onClick={onMarkAllRead}
                className="text-[11px] font-medium text-blue-800 hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div data-testid="notification-empty" className="px-3 py-6 text-center text-[12px] text-ink-400">
              No notifications yet.
            </div>
          ) : (
            items.map((n) => (
              <button
                type="button"
                key={n.id}
                data-testid={`notification-item-${n.id}`}
                onClick={() => onItemClick(n)}
                className={`w-full text-left px-3 py-2.5 border-b border-ink-50 hover:bg-ink-50 transition-colors ${n.is_read ? "" : "bg-blue-50/50"}`}
              >
                <div className="flex items-start gap-2">
                  {!n.is_read && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-800 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold text-ink-950 truncate">{n.title}</div>
                    <div className="text-[11.5px] text-ink-600 mt-0.5">{n.message}</div>
                    <div className="text-[10.5px] text-ink-400 mt-1">{timeAgo(n.created_at)}</div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
